// ARQUIVO ATUALIZADO
import React, { useState, useMemo, useEffect } from 'react';
import GlassCard from '../components/ui/Card';
import GlassButton from '../components/ui/Button';
import { UnarchiveIcon, DeleteIcon, SearchIcon, ProjectIcon, ChevronDownIcon } from '../components/ui/Icons';
import useData from '../hooks/useData';
import DropdownMenu from '../components/ui/DropdownMenu';

const ArchivedTasks = ({ tasks, onUnarchive, onTaskClick, onDeletePermanently }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const { projects, clients } = useData();
    
    // --- INÍCIO DA ALTERAÇÃO: Estados e lógica para os novos filtros ---
    const [selectedClient, setSelectedClient] = useState('all');
    const [selectedProject, setSelectedProject] = useState('all');

    useEffect(() => {
        setSelectedProject('all');
    }, [selectedClient]);

    const clientOptions = useMemo(() => {
        const options = [{ label: 'Todos os Clientes', value: 'all' }];
        const relevantClients = [...new Set(tasks.map(t => t.client))];
        relevantClients.forEach(clientName => {
            if(clientName) options.push({ label: clientName, value: clientName });
        });
        return options;
    }, [tasks]);

    const projectOptions = useMemo(() => {
        const options = [{ label: 'Todos os Projetos', value: 'all' }];
        let projectsToShow = projects;

        if (selectedClient !== 'all') {
            const clientObj = clients.find(c => c.name === selectedClient);
            if (clientObj) {
                projectsToShow = projects.filter(p => p.clientId === clientObj.id);
            } else {
                projectsToShow = []; 
            }
        }

        projectsToShow.forEach(project => {
            options.push({ label: project.name, value: project.id });
        });

        return options;
    }, [projects, clients, selectedClient]);

    const getClientLabel = (value) => value === 'all' ? 'Todos os Clientes' : value;
    const getProjectLabel = (value) => value === 'all' ? 'Todos os Projetos' : projects.find(p => p.id === value)?.name || 'Todos os Projetos';
    // --- FIM DA ALTERAÇÃO ---

    const getArchiveDate = (task) => {
        if (!task.history || task.history.length === 0) return null;
        const archiveAction = [...task.history].reverse().find(h => h.action.includes('arquivou a tarefa'));
        return archiveAction ? new Date(archiveAction.timestamp) : null;
    };

    const groupedAndFilteredTasks = useMemo(() => {
        const lowercasedFilter = searchTerm.toLowerCase();
        
        const filteredTasks = tasks
            .filter(task => selectedClient === 'all' || task.client === selectedClient)
            .filter(task => selectedProject === 'all' || task.projectId === selectedProject)
            .filter(task => {
                const project = projects.find(p => p.id === task.projectId);
                return (
                    task.title.toLowerCase().includes(lowercasedFilter) ||
                    (task.client && task.client.toLowerCase().includes(lowercasedFilter)) ||
                    (project && project.name.toLowerCase().includes(lowercasedFilter))
                );
            });

        const grouped = filteredTasks.reduce((acc, task) => {
            const archiveDate = getArchiveDate(task);
            const monthYear = archiveDate 
                ? archiveDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
                : 'Data Desconhecida';
            
            const capitalizedMonthYear = monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
            if (!acc[capitalizedMonthYear]) acc[capitalizedMonthYear] = [];
            acc[capitalizedMonthYear].push({ ...task, archiveDate });
            return acc;
        }, {});

        for (const month in grouped) {
            grouped[month].sort((a, b) => (b.archiveDate || 0) - (a.archiveDate || 0));
        }
        
        const sortedGroupKeys = Object.keys(grouped).sort((a, b) => {
            const dateA = grouped[a][0].archiveDate || 0;
            const dateB = grouped[b][0].archiveDate || 0;
            return dateB - dateA;
        });

        const sortedGroupedTasks = {};
        for (const key of sortedGroupKeys) {
            sortedGroupedTasks[key] = grouped[key];
        }

        return sortedGroupedTasks;
    }, [tasks, searchTerm, projects, clients, selectedClient, selectedProject]);

    return (
        <div className="p-4 space-y-4 h-full flex flex-col">
            <div className="flex justify-between items-center mb-6 flex-shrink-0">
                <h2 className="text-3xl font-bold text-white">Tarefas Arquivadas</h2>
                {/* --- INÍCIO DA ALTERAÇÃO: Adicionados os novos filtros --- */}
                <div className="flex items-center gap-2">
                    <div className="relative w-full max-w-xs">
                        <input type="text" placeholder="Pesquisar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full bg-white/10 text-white rounded-full py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                        <SearchIcon size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                     <DropdownMenu
                        trigger={
                            <button className="flex items-center justify-between w-48 bg-white/10 text-white text-sm rounded-full py-2 px-3 border border-transparent hover:border-white/20 transition-colors">
                                <span className="truncate">{getClientLabel(selectedClient)}</span>
                                <ChevronDownIcon size={20} className="ml-2 text-gray-400" />
                            </button>
                        }
                    >
                        <div className="max-h-60 w-48 overflow-y-auto">
                            {clientOptions.map(option => (
                                <button key={option.value} onClick={() => setSelectedClient(option.value)} className="w-full text-left px-3 py-2 text-sm text-white hover:bg-indigo-600">
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </DropdownMenu>
                     <DropdownMenu
                        trigger={
                            <button className="flex items-center justify-between w-48 bg-white/10 text-white text-sm rounded-full py-2 px-3 border border-transparent hover:border-white/20 transition-colors">
                                <span className="truncate">{getProjectLabel(selectedProject)}</span>
                                <ChevronDownIcon size={20} className="ml-2 text-gray-400" />
                            </button>
                        }
                    >
                        <div className="max-h-60 w-48 overflow-y-auto">
                            {projectOptions.map(option => (
                                <button key={option.value} onClick={() => setSelectedProject(option.value)} className="w-full text-left px-3 py-2 text-sm text-white hover:bg-indigo-600">
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </DropdownMenu>
                </div>
                {/* --- FIM DA ALTERAÇÃO --- */}
            </div>
            
            <div className="overflow-y-auto flex-grow pr-2 -mr-2">
                {Object.keys(groupedAndFilteredTasks).length > 0 ? (
                    Object.entries(groupedAndFilteredTasks).map(([month, monthTasks]) => (
                        <div key={month} className="mb-8">
                            <h3 className="text-xl font-bold text-gray-300 mb-4 border-b border-white/10 pb-2">{month}</h3>
                            <div className="space-y-3">
                                {monthTasks.map(task => {
                                    const project = projects.find(p => p.id === task.projectId);
                                    return (
                                        <GlassCard key={task.id} className="flex justify-between items-center p-3">
                                            <div className="flex-1 cursor-pointer" onClick={() => onTaskClick(task)}>
                                                <h4 className="font-semibold text-white uppercase">{task.title}</h4>
                                                <div className="flex items-center gap-4 text-xs text-gray-400 mt-1">
                                                    <span>{task.client}</span>
                                                    {project && <span className="flex items-center gap-1.5"><ProjectIcon size={12}/> {project.name}</span>}
                                                </div>
                                                {task.archiveDate && (
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Arquivada em: {task.archiveDate.toLocaleDateString('pt-BR')}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex gap-2">
                                                <GlassButton onClick={() => onUnarchive(task.id)} variant="minimal" title="Desarquivar">
                                                    <UnarchiveIcon size={20} />
                                                </GlassButton>
                                                <GlassButton onClick={() => onDeletePermanently(task)} variant="minimal" className="hover:!bg-red-500/20 hover:!text-red-400" title="Excluir Permanente">
                                                    <DeleteIcon size={20} />
                                                </GlassButton>
                                            </div>
                                        </GlassCard>
                                    );
                                })}
                            </div>
                        </div>
                    ))
                ) : (
                    <p className="text-gray-400 text-center pt-16">Nenhuma tarefa arquivada encontrada para os filtros selecionados.</p>
                )}
            </div>
        </div>
    );
};

export default ArchivedTasks;